import { forwardRef, useLayoutEffect, useRef, useImperativeHandle } from "react";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

interface AutoExpandingTextareaProps extends React.ComponentProps<"textarea"> {
  minRows?: number;
  maxRows?: number;
}

export const AutoExpandingTextarea = forwardRef<HTMLTextAreaElement, AutoExpandingTextareaProps>(
  ({ minRows = 1, maxRows = Infinity, className, value, onChange, ...rest }, ref) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    useImperativeHandle(ref, () => textareaRef.current!);

    const adjustHeight = () => {
      const textarea = textareaRef.current;
      if (!textarea || typeof window === 'undefined') return;

      textarea.style.height = 'auto';
      const scrollHeight = textarea.scrollHeight;
      
      const computedStyle = window.getComputedStyle(textarea);
      const lineHeight = parseFloat(computedStyle.lineHeight) || 24;
      
      const minHeight = lineHeight * minRows;
      const maxHeight = maxRows === Infinity ? scrollHeight + 2 : lineHeight * maxRows;
      
      const newHeight = Math.min(Math.max(scrollHeight + 2, minHeight), maxHeight);
      textarea.style.height = `${newHeight}px`;
      
      if (scrollHeight > maxHeight) {
        textarea.style.overflowY = 'auto';
      } else {
        textarea.style.overflowY = 'hidden';
      }
    };

    useLayoutEffect(() => {
      adjustHeight();
    }, [value, minRows, maxRows]);

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      onChange?.(e);
      adjustHeight();
    };

    return (
      <Textarea
        ref={textareaRef}
        className={cn("resize-none", className)}
        rows={minRows}
        value={value}
        onChange={handleChange}
        {...rest}
      />
    );
  }
);

AutoExpandingTextarea.displayName = "AutoExpandingTextarea";
